/**
 * Digiping activity events — append-only log that feeds Measurable / Time-bound
 * progress after onboarding (home, live training, presence, pose).
 */

export const ACTIVITY_TYPES = Object.freeze({
  // Onboarding / SMART slot milestones
  GOAL_SET: 'GOAL_SET',
  BASELINE_SET: 'BASELINE_SET',
  METRIC_SET: 'METRIC_SET',
  TRAINING_VOLUME_SET: 'TRAINING_VOLUME_SET',
  CONTEXT_SET: 'CONTEXT_SET',
  MILESTONES_SET: 'MILESTONES_SET',
  COACH_AGREEMENT_REQUESTED: 'COACH_AGREEMENT_REQUESTED',
  COACH_AGREEMENT_SET: 'COACH_AGREEMENT_SET',
  IDENTITY_SET: 'IDENTITY_SET',
  CYCLE_STARTED: 'CYCLE_STARTED',

  // Post-onboarding (Active phase)
  SESSION_ATTENDED: 'SESSION_ATTENDED',
  DRILL_COMPLETED: 'DRILL_COMPLETED',
  POSE_METRIC_RECORDED: 'POSE_METRIC_RECORDED',
  GOAL_CHECKIN: 'GOAL_CHECKIN',
  CYCLE_ENDED: 'CYCLE_ENDED',
  CYCLE_RENEWED: 'CYCLE_RENEWED',
  GOALS_REFINED: 'GOALS_REFINED',
  /** Coach-approved catalogue exercise linked to the player's SMART goal. */
  EXERCISE_SELECTED_FOR_GOAL: 'EXERCISE_SELECTED_FOR_GOAL'
});

/** Dimensions primarily affected by each activity type. */
export const ACTIVITY_DIMENSION_IMPACT = Object.freeze({
  [ACTIVITY_TYPES.GOAL_SET]: ['S'],
  [ACTIVITY_TYPES.BASELINE_SET]: ['M'],
  [ACTIVITY_TYPES.METRIC_SET]: ['M'],
  [ACTIVITY_TYPES.TRAINING_VOLUME_SET]: ['M', 'T'],
  [ACTIVITY_TYPES.CONTEXT_SET]: ['A'],
  [ACTIVITY_TYPES.MILESTONES_SET]: ['A'],
  [ACTIVITY_TYPES.COACH_AGREEMENT_REQUESTED]: ['A', 'R'],
  [ACTIVITY_TYPES.COACH_AGREEMENT_SET]: ['A', 'R'],
  [ACTIVITY_TYPES.IDENTITY_SET]: [],
  [ACTIVITY_TYPES.CYCLE_STARTED]: ['T'],
  [ACTIVITY_TYPES.SESSION_ATTENDED]: ['M', 'T'],
  [ACTIVITY_TYPES.DRILL_COMPLETED]: ['M', 'S'],
  [ACTIVITY_TYPES.POSE_METRIC_RECORDED]: ['M'],
  [ACTIVITY_TYPES.GOAL_CHECKIN]: ['M', 'T'],
  [ACTIVITY_TYPES.CYCLE_ENDED]: ['T'],
  [ACTIVITY_TYPES.CYCLE_RENEWED]: ['T'],
  [ACTIVITY_TYPES.GOALS_REFINED]: ['S', 'A'],
  [ACTIVITY_TYPES.EXERCISE_SELECTED_FOR_GOAL]: ['S', 'A', 'M']
});

/**
 * @param {string} type - ACTIVITY_TYPES value
 * @param {object} [payload]
 * @returns {{ id: string, type: string, at: string, payload: object }}
 */
export function createActivityEvent(type, payload = {}) {
  return {
    id: `act_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    type,
    at: new Date().toISOString(),
    payload: payload && typeof payload === 'object' ? payload : {}
  };
}

/**
 * Rough cycle progress for Active-phase Digiping UI (0–1).
 * Counts measurable activities within the current cycle window.
 * Prefer `presenceRatioFromCyclePresence` when API cyclePresence is available (T vs plan).
 */
export function estimateCycleProgress(activities = [], cycleStart, cycleWeeks = 4) {
  if (!cycleStart) return 0;
  const start = new Date(cycleStart).getTime();
  if (Number.isNaN(start)) return 0;
  const end = start + cycleWeeks * 7 * 24 * 60 * 60 * 1000;
  const now = Date.now();
  const timeRatio = Math.min(1, Math.max(0, (now - start) / (end - start || 1)));

  const measurable = new Set([
    ACTIVITY_TYPES.SESSION_ATTENDED,
    ACTIVITY_TYPES.DRILL_COMPLETED,
    ACTIVITY_TYPES.POSE_METRIC_RECORDED,
    ACTIVITY_TYPES.GOAL_CHECKIN,
    ACTIVITY_TYPES.EXERCISE_SELECTED_FOR_GOAL
  ]);

  const inCycle = activities.filter((a) => {
    const t = new Date(a.at).getTime();
    return measurable.has(a.type) && t >= start && t <= end;
  });

  const activityRatio = Math.min(1, inCycle.length / 8);
  return Math.round((timeRatio * 0.35 + activityRatio * 0.65) * 100) / 100;
}

/**
 * T progress from backend presence vs plan (0–1).
 * @param {{ score?: number|null }} [cyclePresence]
 */
export function presenceRatioFromCyclePresence(cyclePresence) {
  if (cyclePresence?.score == null || Number.isNaN(Number(cyclePresence.score))) {
    return null;
  }
  return Math.min(1, Math.max(0, Number(cyclePresence.score) / 100));
}
